import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Chip,
  Divider,
  Alert,
  Snackbar,
  Tooltip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import SendIcon from '@mui/icons-material/Send';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import BuildIcon from '@mui/icons-material/Build';
import EventIcon from '@mui/icons-material/Event';
import EditNoteIcon from '@mui/icons-material/EditNote';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import HistoryIcon from '@mui/icons-material/History';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RepeatIcon from '@mui/icons-material/Repeat';
import DialogActions from '@mui/material/DialogActions';
import { createShiftSummary, updateShiftSummary, getShiftSummaryById, getShiftSummaryByDateAndShift, listShiftSummaries, subscribeToShiftUpdates, ShiftSummaryRecord } from '../services/shiftApi';
import { getTrash, addToTrash, restoreFromTrash, permanentlyDelete, clearTrash } from '../services/trashService';
import {
  ShiftType,
  SHIFT_LABELS,
  CUSTOMERS,
  VALID_MAINTENANCE_CUSTOMERS,
  ZNUNY_INSTANCES,
  UrgentTicket,
  CurrentTicket,
  MaintenanceItem,
  detectShift,
  getLocalDateString,
  getShiftStart,
  getPreviousShift,
  getShiftRecordId,
  Customer,
  TrashedTicket,
} from '../types/shift';

// Customer colors matching the Google Sheet
const CUSTOMER_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  HCE:              { bg: 'rgba(76, 175, 80, 0.10)',  border: '#4caf50', text: '#81c784' },
  NAN:              { bg: 'rgba(66, 133, 244, 0.10)', border: '#4285f4', text: '#90caf9' },
  Hiboo:            { bg: 'rgba(255, 152, 0, 0.10)',  border: '#ff9800', text: '#ffb74d' },
  Flex:             { bg: 'rgba(255, 235, 59, 0.08)', border: '#fdd835', text: '#fff176' },
  NOCLAND:          { bg: 'rgba(0, 188, 212, 0.10)',  border: '#00bcd4', text: '#4dd0e1' },
  'Service Request':{ bg: 'rgba(76, 175, 80, 0.06)',  border: '#66bb6a', text: '#a5d6a7' },
  Unassigned:       { bg: 'rgba(158, 158, 158, 0.08)',border: '#616161', text: '#9e9e9e' },
};

const getCustomerColor = (customer: string) =>
  CUSTOMER_COLORS[customer] || CUSTOMER_COLORS.Unassigned;

const ALL_CUSTOMERS = [...CUSTOMERS, 'Service Request'] as string[];
const MAINTENANCE_CUSTOMERS = [...VALID_MAINTENANCE_CUSTOMERS] as string[];

/** Strip prefixes like "Ticket#" or "#Ticket#" so stored value is just the numeric ID */
const normalizeTicketNumber = (raw: string): string =>
  raw.replace(/^#?(?:Ticket#)+/i, '').trim();

const emptyUrgentTicket = (): UrgentTicket => ({
  customer: '',
  ticketNumber: '',
  ticketSubject: '',
  whatNeedsToBeDone: '',
  znunyInstance: '',
  resolved: false,
});

const emptyCurrentTicket = (): CurrentTicket => ({
  customer: '',
  ticketNumber: '',
  ticketSubject: '',
  actionSummary: '',
  nextSteps: '',
  znunyInstance: '',
  carriedForward: false,
  carriedForwardCount: 0,
  firstAppearedDate: new Date().toISOString().split('T')[0],
});

const emptyMaintenanceItem = (): MaintenanceItem => ({
  customer: '',
  ticketNumber: '',
  description: '',
  status: '',
  scheduledDate: null,
  scheduledTime: null,
});

// Compact inline text field with minimal chrome
const InlineField: React.FC<{
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  mono?: boolean;
  multiline?: boolean;
  sx?: object;
}> = ({ value, onChange, placeholder, mono, multiline, sx }) => (
  <TextField
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    size="small"
    multiline={multiline}
    maxRows={3}
    fullWidth
    variant="standard"
    InputProps={{
      disableUnderline: true,
      sx: {
        fontSize: '0.8125rem',
        fontFamily: mono ? '"JetBrains Mono", "Fira Code", "SF Mono", monospace' : undefined,
        color: '#e2e8f0',
        px: 1,
        py: 0.5,
        borderRadius: '4px',
        '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
        '&.Mui-focused': { bgcolor: 'rgba(0,188,212,0.06)', outline: '1px solid rgba(0,188,212,0.3)' },
        ...sx,
      },
    }}
  />
);

const ShiftHandoffForm: React.FC = () => {
  const { user } = useAuthenticator();
  const navigate = useNavigate();
  const loginId = user?.signInDetails?.loginId ?? '';
  // Derive display name from email: "finn.stephen@gmail.com" -> "Finn Stephen"
  const loggedInName = loginId.includes('@')
    ? loginId.split('@')[0].split('.').map((p: string) => p.charAt(0).toUpperCase() + p.slice(1)).join(' ')
    : loginId || 'Unknown';
  const today = getLocalDateString();

  const [date, setDate] = useState(today);
  const [shift, setShift] = useState<ShiftType>(detectShift());
  const [pagerDutyOnCall, setPagerDutyOnCall] = useState('');
  const [existingRecordId, setExistingRecordId] = useState<string | null>(null);
  const [contributorsList, setContributorsList] = useState<string[]>([]);
  const [urgentTickets, setUrgentTickets] = useState<UrgentTicket[]>([]);
  const [currentTickets, setCurrentTickets] = useState<CurrentTicket[]>([]);
  const [maintenanceItems, setMaintenanceItems] = useState<MaintenanceItem[]>([]);
  const [notes, setNotes] = useState('');
  const [expandedTicketIdx, setExpandedTicketIdx] = useState<number | null>(null);
  const [expandedUrgentIdx, setExpandedUrgentIdx] = useState<number | null>(null);
  const [expandedMaintIdx, setExpandedMaintIdx] = useState<number | null>(null);
  const [trashItems, setTrashItems] = useState<TrashedTicket[]>([]);
  const [trashExpanded, setTrashExpanded] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historySummaries, setHistorySummaries] = useState<ShiftSummaryRecord[]>([]);
  const [historyExpandedId, setHistoryExpandedId] = useState<string | null>(null);
  const [statusQuoConfirmOpen, setStatusQuoConfirmOpen] = useState(false);
  const lastDeletedRef = useRef<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // ── Deep-link filter (from Dashboard / Calendar drill-downs) ──
  const [searchParams, setSearchParams] = useSearchParams();
  type FilterType = 'urgent' | 'stale' | 'customer' | 'maintenance';
  const [filterHighlight, setFilterHighlight] = useState<{ type: FilterType; value?: string } | null>(null);

  const isStale = useCallback((ticket: CurrentTicket): boolean => {
    if (!ticket.firstAppearedDate) return false;
    const first = new Date(ticket.firstAppearedDate);
    if (Number.isNaN(first.getTime())) return false;
    const ageDays = (Date.now() - first.getTime()) / 86_400_000;
    return ageDays >= 7;
  }, []);

  useEffect(() => {
    const filter = searchParams.get('filter') as FilterType | null;
    if (!filter) {
      setFilterHighlight(null);
      return;
    }
    const value = searchParams.get('customer') || searchParams.get('ticket') || undefined;

    let targetId: string | null = null;
    if (filter === 'urgent') targetId = 'section-urgent';
    else if (filter === 'stale') targetId = 'section-current-tickets';
    else if (filter === 'customer' && value) targetId = `customer-group-${value}`;
    else if (filter === 'maintenance') targetId = value ? `maintenance-ticket-${value}` : 'section-maintenance';

    setFilterHighlight({ type: filter, value });

    // Scroll after initial data/render has landed.
    const scrollTimer = setTimeout(() => {
      if (!targetId) return;
      const el = document.getElementById(targetId);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 400);

    // Keep the URL clean — subsequent reloads shouldn't re-scroll.
    setSearchParams({}, { replace: true });

    // Auto-clear the highlight.
    const clearTimer = setTimeout(() => setFilterHighlight(null), 5000);

    return () => {
      clearTimeout(scrollTimer);
      clearTimeout(clearTimer);
    };
  }, [searchParams, setSearchParams]);

  // ──── Undo / Redo history stack ────
  interface FormSnapshot {
    urgentTickets: UrgentTicket[];
    currentTickets: CurrentTicket[];
    maintenanceItems: MaintenanceItem[];
    notes: string;
  }
  const MAX_HISTORY = 50;
  const historyRef = useRef<FormSnapshot[]>([]);
  const historyIndexRef = useRef(-1);
  const historyDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isRestoringRef = useRef(false);

  const getSnapshot = useCallback((): FormSnapshot => ({
    urgentTickets: JSON.parse(JSON.stringify(urgentTickets)),
    currentTickets: JSON.parse(JSON.stringify(currentTickets)),
    maintenanceItems: JSON.parse(JSON.stringify(maintenanceItems)),
    notes,
  }), [urgentTickets, currentTickets, maintenanceItems, notes]);

  const restoreSnapshot = useCallback((snap: FormSnapshot) => {
    isRestoringRef.current = true;
    setUrgentTickets(snap.urgentTickets);
    setCurrentTickets(snap.currentTickets);
    setMaintenanceItems(snap.maintenanceItems);
    setNotes(snap.notes);
    setExpandedTicketIdx(null);
    setExpandedUrgentIdx(null);
    setExpandedMaintIdx(null);
    setTimeout(() => { isRestoringRef.current = false; }, 100);
  }, []);

  // Push snapshot on changes (debounced 1.5s for typing, immediate for add/delete)
  const pushHistory = useCallback((immediate = false) => {
    if (isRestoringRef.current) return;
    const push = () => {
      const snap = getSnapshot();
      const hist = historyRef.current;
      const idx = historyIndexRef.current;
      // Don't push if identical to current
      if (idx >= 0 && JSON.stringify(hist[idx]) === JSON.stringify(snap)) return;
      // Truncate any redo states
      historyRef.current = hist.slice(0, idx + 1);
      historyRef.current.push(snap);
      if (historyRef.current.length > MAX_HISTORY) historyRef.current.shift();
      historyIndexRef.current = historyRef.current.length - 1;
    };
    if (immediate) {
      if (historyDebounceRef.current) clearTimeout(historyDebounceRef.current);
      push();
    } else {
      if (historyDebounceRef.current) clearTimeout(historyDebounceRef.current);
      historyDebounceRef.current = setTimeout(push, 1500);
    }
  }, [getSnapshot]);

  // Track changes — debounced for typing
  useEffect(() => {
    if (!draftLoadedRef.current || isRestoringRef.current) return;
    pushHistory(false);
  }, [urgentTickets, currentTickets, maintenanceItems, notes, pushHistory]);

  const canUndo = historyIndexRef.current > 0;
  const canRedo = historyIndexRef.current < historyRef.current.length - 1;

  const handleUndo = useCallback(() => {
    if (historyIndexRef.current <= 0) return;
    historyIndexRef.current -= 1;
    restoreSnapshot(historyRef.current[historyIndexRef.current]);
    setSnackbar({ open: true, message: 'Undone', severity: 'success' });
  }, [restoreSnapshot]);

  const handleRedo = useCallback(() => {
    if (historyIndexRef.current >= historyRef.current.length - 1) return;
    historyIndexRef.current += 1;
    restoreSnapshot(historyRef.current[historyIndexRef.current]);
    setSnackbar({ open: true, message: 'Redone', severity: 'success' });
  }, [restoreSnapshot]);

  // Keyboard shortcuts: Ctrl+Z / Ctrl+Shift+Z
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'z' && e.shiftKey) {
        e.preventDefault();
        handleRedo();
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
        e.preventDefault();
        handleRedo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleUndo, handleRedo]);

  // Load trash on mount
  const refreshTrash = useCallback(() => setTrashItems(getTrash()), []);
  useEffect(() => { refreshTrash(); }, [refreshTrash]);

  // Delete handler — moves to trash
  const handleDelete = (type: 'urgent' | 'current' | 'maintenance', index: number) => {
    pushHistory(true);
    let ticket: UrgentTicket | CurrentTicket | MaintenanceItem;
    let ticketNum = '';
    if (type === 'urgent') {
      ticket = urgentTickets[index];
      ticketNum = ticket?.ticketNumber || '';
      setUrgentTickets((prev) => prev.filter((_, i) => i !== index));
      setExpandedUrgentIdx(null);
    } else if (type === 'current') {
      ticket = currentTickets[index];
      ticketNum = ticket?.ticketNumber || '';
      setCurrentTickets((prev) => prev.filter((_, i) => i !== index));
      setExpandedTicketIdx(null);
    } else {
      ticket = maintenanceItems[index];
      ticketNum = ticket?.ticketNumber || '';
      setMaintenanceItems((prev) => prev.filter((_, i) => i !== index));
      setExpandedMaintIdx(null);
    }
    const trashId = addToTrash({ type, ticket, deletedAt: new Date().toISOString(), deletedFromShift: `${date}-${shift}`, deletedBy: loggedInName });
    lastDeletedRef.current = trashId;
    refreshTrash();
    const label = type === 'maintenance' ? 'maintenance item' : 'ticket';
    setSnackbar({ open: true, message: `Deleted ${label}${ticketNum ? ` #${ticketNum}` : ''}`, severity: 'success' });
  };

  // Restore the last deleted item (for snackbar button)
  const handleRestoreLast = () => {
    if (!lastDeletedRef.current) return;
    const restored = restoreFromTrash(lastDeletedRef.current);
    if (!restored) return;
    if (restored.type === 'urgent') setUrgentTickets((prev) => [...prev, restored.ticket as UrgentTicket]);
    else if (restored.type === 'current') setCurrentTickets((prev) => [...prev, restored.ticket as CurrentTicket]);
    else setMaintenanceItems((prev) => [...prev, restored.ticket as MaintenanceItem]);
    lastDeletedRef.current = null;
    refreshTrash();
    setSnackbar({ open: true, message: 'Restored', severity: 'success' });
  };

  // Restore any item from trash
  const handleRestoreFromTrash = (id: string) => {
    const restored = restoreFromTrash(id);
    if (!restored) return;
    if (restored.type === 'urgent') setUrgentTickets((prev) => [...prev, restored.ticket as UrgentTicket]);
    else if (restored.type === 'current') setCurrentTickets((prev) => [...prev, restored.ticket as CurrentTicket]);
    else setMaintenanceItems((prev) => [...prev, restored.ticket as MaintenanceItem]);
    refreshTrash();
    const tn = 'ticketNumber' in restored.ticket ? restored.ticket.ticketNumber : '';
    setSnackbar({ open: true, message: `Restored${tn ? ` #${tn}` : ''}`, severity: 'success' });
  };

  // Permanently remove from trash
  const handlePermanentDelete = (id: string) => {
    permanentlyDelete(id);
    refreshTrash();
  };

  // Clear all trash
  const handleClearTrash = () => {
    clearTrash();
    refreshTrash();
  };

  // Relative time helper
  const relativeTime = (iso: string) => {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  const [previousShiftInfo, setPreviousShiftInfo] = useState<{ tech: string; shift: string } | null>(null);
  const [autoLoaded, setAutoLoaded] = useState(false);
  const [draftStatus, setDraftStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftLoadedRef = useRef(false);

  // ──── Auto-save draft to localStorage ────
  const DRAFT_KEY = `noc-shift-draft-${date}-${shift}`;

  const saveDraft = useCallback(() => {
    const draft = {
      urgentTickets,
      currentTickets,
      maintenanceItems,
      notes,
      savedAt: new Date().toISOString(),
    };
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
      setDraftStatus('saved');
    } catch { /* localStorage full — ignore */ }
  }, [DRAFT_KEY, urgentTickets, currentTickets, maintenanceItems, notes]);

  // Debounced auto-save: save 1s after last change
  useEffect(() => {
    if (!draftLoadedRef.current) return; // Don't save during initial load
    setDraftStatus('saving');
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(() => saveDraft(), 1000);
    return () => { if (draftTimerRef.current) clearTimeout(draftTimerRef.current); };
  }, [urgentTickets, currentTickets, maintenanceItems, notes, saveDraft]);

  // Restore draft on mount (user edits like notes/text), but always let API refresh carry metadata.
  // Re-runs on shift rollover so the right draft is rehydrated for the new shift.
  useEffect(() => {
    draftLoadedRef.current = false;
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) {
        const draft = JSON.parse(saved);
        if (draft.urgentTickets?.length) setUrgentTickets(draft.urgentTickets);
        if (draft.currentTickets?.length) setCurrentTickets(draft.currentTickets);
        if (draft.maintenanceItems?.length) setMaintenanceItems(draft.maintenanceItems);
        if (draft.notes) setNotes(draft.notes);
        setDraftStatus('saved');
      }
    } catch { /* corrupt draft — ignore */ }
    const t = setTimeout(() => { draftLoadedRef.current = true; }, 500);
    return () => clearTimeout(t);
  }, [DRAFT_KEY]);

  // Re-evaluate current date/shift on focus/visibility/interval so a tab left open
  // across a shift boundary refetches for the new shift (MFE-92).
  useEffect(() => {
    const check = () => {
      const now = new Date();
      const nextDate = getLocalDateString(now);
      const nextShift = detectShift(now);
      if (nextDate !== date || nextShift !== shift) {
        setDate(nextDate);
        setShift(nextShift);
        setAutoLoaded(false);
        setExistingRecordId(null);
        setContributorsList([]);
      }
    };
    const onVisibility = () => { if (document.visibilityState === 'visible') check(); };
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', onVisibility);
    const interval = setInterval(check, 60_000);
    return () => {
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(interval);
    };
  }, [date, shift]);

  // Fetch current on-call from PagerDuty
  useEffect(() => {
    (async () => {
      try {
        const resp = await fetch('https://zo421qtcy7.execute-api.us-east-1.amazonaws.com/oncall');
        const data = await resp.json();
        const oncalls: { name: string; email: string }[] = data.oncalls || [];
        // Deduplicate
        const unique = [...new Map(oncalls.map((o) => [o.email, o])).values()];
        if (unique.length > 0 && !pagerDutyOnCall) {
          setPagerDutyOnCall(unique.map((o) => o.name).join(', '));
        }
      } catch (err) {
        console.warn('Failed to fetch PagerDuty on-call:', err);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load current shift record (if exists) or carry-forward from previous shift
  useEffect(() => {
    if (autoLoaded) return;
    let cancelled = false;

    const parse = (json: string | null | undefined) => {
      try {
        let p = JSON.parse(json ?? '[]');
        if (typeof p === 'string') p = JSON.parse(p);
        return Array.isArray(p) ? p : [];
      } catch { return []; }
    };

    (async () => {
      try {
        // Step 1: Check if a record already exists for this date+shift
        const existing =
          (await getShiftSummaryById(getShiftRecordId(date, shift))) ??
          (await getShiftSummaryByDateAndShift(date, shift));
        if (cancelled) return;

        if (existing) {
          // Load the existing shared shift record into the form
          setExistingRecordId(existing.id);
          // Load contributors list
          try {
            let c = JSON.parse(existing.contributors as string ?? '[]');
            if (typeof c === 'string') c = JSON.parse(c);
            if (Array.isArray(c)) setContributorsList(c);
          } catch { /* ignore */ }
          const loadedCurrent = parse(existing.currentTicketsJson).map((t: CurrentTicket) => ({ ...emptyCurrentTicket(), ...t }));
          const loadedUrgent = parse(existing.urgentTicketsJson).map((t: UrgentTicket) => ({ ...emptyUrgentTicket(), ...t }));
          const loadedMaintenance = parse(existing.maintenanceItemsJson).map((t: MaintenanceItem) => ({ ...emptyMaintenanceItem(), ...t }));

          if (!cancelled) {
            const apiHasData = loadedCurrent.length > 0 || loadedUrgent.length > 0 || loadedMaintenance.length > 0;
            if (apiHasData) {
              // API record has tickets — use it as the source of truth
              setCurrentTickets(loadedCurrent);
              setUrgentTickets(loadedUrgent);
              setMaintenanceItems(loadedMaintenance);
              setNotes(existing.notes || '');
              localStorage.removeItem(DRAFT_KEY);
            } else {
              // API record exists but is empty — keep localStorage draft if it has data
              setCurrentTickets((draft) => draft.length > 0 ? draft : loadedCurrent);
              setUrgentTickets((draft) => draft.length > 0 ? draft : loadedUrgent);
              setMaintenanceItems((draft) => draft.length > 0 ? draft : loadedMaintenance);
              if (existing.notes) setNotes((n) => n || existing.notes || '');
            }
            setPreviousShiftInfo({ tech: existing.submittedBy || existing.onDutyTech, shift: existing.shift });
            setAutoLoaded(true);
          }
          return;
        }

        // Step 2: No record for current shift — carry forward from most recent previous shift
        // Walk backwards up to 6 shifts (2 days) to find a record
        let previousSummary: ShiftSummaryRecord | null = null;
        let prevDate = date;
        let prevShift = shift;
        for (let i = 0; i < 6; i++) {
          const prev = getPreviousShift(prevDate, prevShift);
          prevDate = prev.date;
          prevShift = prev.shift;
          previousSummary = await getShiftSummaryByDateAndShift(prevDate, prevShift);
          if (cancelled) return;
          if (previousSummary) break;
        }

        if (previousSummary) {
          setPreviousShiftInfo({ tech: previousSummary.submittedBy || previousSummary.onDutyTech, shift: previousSummary.shift });

          const prevCurrent = parse(previousSummary.currentTicketsJson).map((t: CurrentTicket) => {
            // Compute carry count from firstAppearedDate for accuracy
            const appeared = t.firstAppearedDate || previousSummary.date;
            const daysSince = Math.max(1, Math.floor(
              (Date.now() - new Date(appeared).getTime()) / (1000 * 60 * 60 * 8),
            )); // ~1 per shift (8hr windows)
            return {
              ...emptyCurrentTicket(),
              ...t,
              firstAppearedDate: appeared,
              carriedForward: true,
              carriedForwardCount: daysSince,
            };
          });
          const prevUrgent = parse(previousSummary.urgentTicketsJson)
            .filter((t: UrgentTicket) => !t.resolved)
            .map((t: UrgentTicket) => ({
              ...emptyUrgentTicket(),
              ...t,
            }));
          const prevMaintenance = parse(previousSummary.maintenanceItemsJson).map((t: MaintenanceItem) => ({
            ...emptyMaintenanceItem(),
            ...t,
          }));

          if (!cancelled) {
            // Build a map of carry metadata from API
            const carryMap: Record<string, { carriedForwardCount: number; firstAppearedDate: string; lastUpdatedBy?: string }> = {};
            for (const t of prevCurrent) {
              carryMap[t.ticketNumber] = {
                carriedForwardCount: t.carriedForwardCount,
                firstAppearedDate: t.firstAppearedDate,
                lastUpdatedBy: t.lastUpdatedBy,
              };
            }

            // If we already have tickets (from localStorage draft), merge carry metadata
            setCurrentTickets((existing) => {
              if (existing.length > 0) {
                return existing.map((t) => {
                  const meta = carryMap[t.ticketNumber];
                  if (meta) {
                    return { ...t, carriedForward: true, carriedForwardCount: meta.carriedForwardCount, firstAppearedDate: meta.firstAppearedDate || t.firstAppearedDate, lastUpdatedBy: t.lastUpdatedBy || meta.lastUpdatedBy };
                  }
                  return t;
                });
              }
              return prevCurrent.length > 0 ? prevCurrent : existing;
            });
            setUrgentTickets((existing) => existing.length > 0 ? existing : prevUrgent);
            setMaintenanceItems((existing) => existing.length > 0 ? existing : prevMaintenance);
            setAutoLoaded(true);
          }
        } else {
          // No previous shift data found either
          if (!cancelled) setAutoLoaded(true);
        }
      } catch (err) {
        console.error('Failed to auto-load shift data:', err);
        if (!cancelled) setAutoLoaded(true);
      }
    })();

    return () => { cancelled = true; };
  }, [autoLoaded, date, shift]); // eslint-disable-line react-hooks/exhaustive-deps

  // Subscribe to real-time updates from other operators on the same shift
  useEffect(() => {
    if (!existingRecordId) return;

    const parse = (json: string | null | undefined) => {
      try {
        let p = JSON.parse(json ?? '[]');
        if (typeof p === 'string') p = JSON.parse(p);
        return Array.isArray(p) ? p : [];
      } catch { return []; }
    };

    const sub = subscribeToShiftUpdates(existingRecordId, (record) => {
      // Only apply if the update was from a different user
      if (record.lastUpdatedBy === loggedInName) return;

      const remoteCurrent = parse(record.currentTicketsJson).map((t: CurrentTicket) => ({ ...emptyCurrentTicket(), ...t }));
      const remoteUrgent = parse(record.urgentTicketsJson).map((t: UrgentTicket) => ({ ...emptyUrgentTicket(), ...t }));
      const remoteMaintenance = parse(record.maintenanceItemsJson).map((t: MaintenanceItem) => ({ ...emptyMaintenanceItem(), ...t }));

      setCurrentTickets(remoteCurrent);
      setUrgentTickets(remoteUrgent);
      setMaintenanceItems(remoteMaintenance);
      if (record.notes) setNotes(record.notes);
      setSnackbar({ open: true, message: `${record.lastUpdatedBy || 'Someone'} updated the shift summary`, severity: 'info' });
    });

    return () => sub.unsubscribe();
  }, [existingRecordId, loggedInName]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCustomerChange = (
    index: number,
    list: 'urgent' | 'current' | 'maintenance',
    value: string,
  ) => {
    const instance = ZNUNY_INSTANCES[value as Customer] ?? '';
    if (list === 'urgent') {
      setUrgentTickets((prev) =>
        prev.map((t, i) => (i === index ? { ...t, customer: value, znunyInstance: instance } : t)),
      );
    } else if (list === 'current') {
      setCurrentTickets((prev) =>
        prev.map((t, i) => (i === index ? { ...t, customer: value, znunyInstance: instance } : t)),
      );
    } else {
      setMaintenanceItems((prev) =>
        prev.map((t, i) => (i === index ? { ...t, customer: value } : t)),
      );
    }
  };

  const znunyUrl = (instance: string, ticketNumber: string) =>
    instance && ticketNumber
      ? `https://${instance}/otrs/index.pl?Action=AgentTicketZoom;TicketNumber=${ticketNumber}`
      : null;

  const handleSubmit = async () => {
    const missingCustomer = maintenanceItems.some((m) => !m.customer);
    if (missingCustomer) {
      setSnackbar({ open: true, message: 'All maintenance items require a customer', severity: 'error' });
      return;
    }
    setSubmitting(true);
    try {
      const now = new Date().toISOString();
      const updatedContributors = [...new Set([...contributorsList, loggedInName])];
      setContributorsList(updatedContributors);
      const summaryData = {
        date,
        shift,
        shiftStart: getShiftStart(date, shift),
        shiftEnd: now,
        onDutyTech: loggedInName,
        onDutyTechId: user?.userId ?? '',
        submittedBy: loggedInName,
        lastUpdatedBy: loggedInName,
        contributors: JSON.stringify(updatedContributors),
        submittedAt: now,
        urgentTicketsJson: JSON.stringify(urgentTickets),
        currentTicketsJson: JSON.stringify(currentTickets),
        maintenanceItemsJson: JSON.stringify(maintenanceItems),
        notes,
      };

      if (existingRecordId) {
        await updateShiftSummary({ id: existingRecordId, ...summaryData });
      } else {
        const deterministicId = `${date}-${shift}`;
        try {
          await createShiftSummary({ id: deterministicId, ...summaryData });
        } catch {
          // Record already exists (ConditionalCheckFailedException) — fall back to update
          await updateShiftSummary({ id: deterministicId, ...summaryData });
        }
        setExistingRecordId(deterministicId);
      }
      setSnackbar({ open: true, message: 'Shift summary submitted successfully', severity: 'success' });
      localStorage.removeItem(DRAFT_KEY);
      setDraftStatus('idle');
    } catch (err) {
      console.error('Submit failed:', err);
      setSnackbar({ open: true, message: 'Failed to submit shift summary', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusQuo = async () => {
    setSubmitting(true);
    setStatusQuoConfirmOpen(false);
    try {
      const now = new Date().toISOString();
      const deterministicId = getShiftRecordId(date, shift);
      const statusQuoNote = `Status quo — no changes from previous shift (${previousShiftInfo?.shift ?? ''} shift by ${previousShiftInfo?.tech ?? 'unknown'})`;
      const combinedNotes = notes?.trim() ? `${statusQuoNote}\n\n${notes}` : statusQuoNote;

      const summaryData = {
        id: deterministicId,
        date,
        shift,
        shiftStart: getShiftStart(date, shift),
        shiftEnd: now,
        onDutyTech: loggedInName,
        onDutyTechId: user?.userId ?? '',
        submittedBy: loggedInName,
        lastUpdatedBy: loggedInName,
        contributors: JSON.stringify([loggedInName]),
        submittedAt: now,
        urgentTicketsJson: JSON.stringify(urgentTickets),
        currentTicketsJson: JSON.stringify(currentTickets),
        maintenanceItemsJson: JSON.stringify(maintenanceItems),
        notes: combinedNotes,
      };

      try {
        await createShiftSummary(summaryData);
      } catch {
        await updateShiftSummary({ ...summaryData });
      }

      setExistingRecordId(deterministicId);
      setNotes(combinedNotes);
      setSnackbar({ open: true, message: 'Status quo submitted — all items carried forward', severity: 'success' });
      localStorage.removeItem(DRAFT_KEY);
      setDraftStatus('idle');
    } catch (err) {
      console.error('Status quo submit failed:', err);
      setSnackbar({ open: true, message: 'Failed to submit status quo', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // Group tickets by customer, preserving order of first appearance
  const groupedCurrentTickets = (() => {
    const groups: { customer: string; tickets: { ticket: CurrentTicket; globalIdx: number }[] }[] = [];
    const seen: Record<string, number> = {};
    currentTickets.forEach((ticket, idx) => {
      const key = ticket.customer || 'Unassigned';
      if (!(key in seen)) {
        seen[key] = groups.length;
        groups.push({ customer: key, tickets: [] });
      }
      groups[seen[key]].tickets.push({ ticket, globalIdx: idx });
    });
    return groups;
  })();

  // Section header bar component
  const SectionBar: React.FC<{
    title: string;
    color: string;
    count: number;
    onAdd: () => void;
    addLabel: string;
    icon?: React.ReactNode;
    info?: React.ReactNode;
  }> = ({ title, color, count, onAdd, addLabel, icon, info }) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1,
        px: 2,
        mb: 0.5,
        background: `linear-gradient(90deg, ${color}18 0%, transparent 100%)`,
        borderLeft: `3px solid ${color}`,
        borderRadius: '0 6px 6px 0',
      }}
    >
      {icon}
      <Typography
        variant="subtitle2"
        sx={{ color, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: '0.75rem' }}
      >
        {title}
      </Typography>
      <Chip
        label={count}
        size="small"
        sx={{
          height: 20,
          fontSize: '0.7rem',
          fontWeight: 700,
          bgcolor: `${color}22`,
          color,
          border: `1px solid ${color}44`,
        }}
      />
      {info}
      <Box sx={{ flexGrow: 1 }} />
      <Button
        startIcon={<AddIcon sx={{ fontSize: '0.875rem !important' }} />}
        size="small"
        onClick={onAdd}
        sx={{
          fontSize: '0.7rem',
          color,
          borderColor: `${color}44`,
          textTransform: 'none',
          px: 1.5,
          py: 0.25,
          minHeight: 0,
          border: '1px solid',
          borderRadius: '4px',
          '&:hover': { bgcolor: `${color}15`, borderColor: color },
        }}
      >
        {addLabel}
      </Button>
    </Box>
  );

  // Table header row
  const TableHeader: React.FC<{ columns: { label: string; width: string }[] }> = ({ columns }) => (
    <Box
      sx={{
        display: 'flex',
        gap: 0,
        px: 1,
        py: 0.5,
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      {/* Color strip placeholder */}
      <Box sx={{ width: 4, flexShrink: 0 }} />
      {columns.map((col) => (
        <Typography
          key={col.label}
          sx={{
            width: col.width,
            flexShrink: col.width === 'auto' ? undefined : 0,
            flexGrow: col.width === 'auto' ? 1 : 0,
            fontSize: '0.65rem',
            fontWeight: 600,
            color: '#64748b',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            px: 1,
          }}
        >
          {col.label}
        </Typography>
      ))}
      {/* Action buttons placeholder */}
      <Box sx={{ width: 68, flexShrink: 0 }} />
    </Box>
  );

  return (
    <Box>
      {/* Header — compact ops-style bar */}
      <Paper
        sx={{
          p: 2,
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
          borderBottom: '1px solid rgba(0,188,212,0.2)',
        }}
      >
        <Box sx={{ minWidth: 200 }}>
          <Typography sx={{ fontSize: '0.65rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            Logged-In User
          </Typography>
          <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#4dd0e1', px: 0.5, py: 0.5 }}>
            {loggedInName}
          </Typography>
          {pagerDutyOnCall && pagerDutyOnCall !== loggedInName && (
            <Typography sx={{ fontSize: '0.6rem', color: '#64748b', px: 0.5 }}>
              PagerDuty on-call: {pagerDutyOnCall}
            </Typography>
          )}
        </Box>
        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
        <Box>
          <Typography sx={{ fontSize: '0.65rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            Date
          </Typography>
          <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#e2e8f0', px: 0.5 }}>
            {new Date(date + 'T00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
          </Typography>
        </Box>
        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
        <Box>
          <Typography sx={{ fontSize: '0.65rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            Shift
          </Typography>
          <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#e2e8f0', px: 0.5 }}>
            {SHIFT_LABELS[shift]}
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
      </Paper>

      {/* ──────── STATUS QUO ROLLOVER ──────── */}
      {autoLoaded && !existingRecordId && previousShiftInfo && (
        <Paper
          sx={{
            mb: 2,
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            background: 'linear-gradient(135deg, rgba(30,41,59,0.8) 0%, rgba(15,23,42,0.9) 100%)',
            border: '1px solid rgba(245,158,11,0.25)',
            borderRadius: '8px',
          }}
        >
          <RepeatIcon sx={{ fontSize: '1.25rem', color: '#f59e0b' }} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#e2e8f0' }}>
              No changes this shift?
            </Typography>
            <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8' }}>
              Carry forward all {currentTickets.length + urgentTickets.length + maintenanceItems.length} items from {previousShiftInfo.tech}&apos;s {previousShiftInfo.shift} shift unchanged
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RepeatIcon />}
            onClick={() => setStatusQuoConfirmOpen(true)}
            disabled={submitting}
            sx={{
              color: '#f59e0b',
              borderColor: 'rgba(245,158,11,0.4)',
              fontWeight: 600,
              textTransform: 'none',
              px: 2.5,
              whiteSpace: 'nowrap',
              '&:hover': {
                borderColor: '#f59e0b',
                bgcolor: 'rgba(245,158,11,0.08)',
              },
            }}
          >
            No Changes — Carry Forward
          </Button>
        </Paper>
      )}

      {/* ──────── URGENT TICKETS ──────── */}
      <Paper
        id="section-urgent"
        sx={{
          mb: 2,
          overflow: 'hidden',
          bgcolor: 'rgba(15,23,42,0.6)',
          scrollMarginTop: 80,
          ...(filterHighlight?.type === 'urgent' && { boxShadow: '0 0 0 2px #ef4444, 0 0 18px rgba(239,68,68,0.35)' }),
          transition: 'box-shadow 0.4s ease',
        }}
      >
        <SectionBar
          title="Urgent — Immediate Attention"
          color="#ef4444"
          count={urgentTickets.length}
          onAdd={() => setUrgentTickets((prev) => [{ ...emptyUrgentTicket(), lastUpdatedBy: loggedInName }, ...prev])}
          addLabel="Add Urgent"
          icon={<WarningAmberIcon sx={{ fontSize: '0.875rem', color: '#ef4444' }} />}
        />

        {urgentTickets.length === 0 ? (
          <Typography sx={{ px: 3, py: 2, fontSize: '0.8rem', color: '#475569', fontStyle: 'italic' }}>
            No urgent tickets
          </Typography>
        ) : (
          <>
            <TableHeader columns={[
              { label: 'Customer', width: '100px' },
              { label: 'Ticket #', width: '180px' },
              { label: 'Subject', width: 'auto' },
            ]} />
            {urgentTickets.map((ticket, idx) => {
              const colors = getCustomerColor(ticket.customer);
              const link = znunyUrl(ticket.znunyInstance, ticket.ticketNumber);
              const isExpanded = expandedUrgentIdx === idx;
              const hasNotes = !!ticket.whatNeedsToBeDone;
              return (
                <Box key={idx}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0,
                      px: 1,
                      py: 0.25,
                      bgcolor: 'rgba(239, 68, 68, 0.04)',
                      borderBottom: isExpanded ? 'none' : '1px solid rgba(255,255,255,0.03)',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.07)' },
                      transition: 'background 0.15s',
                      ...(isExpanded && { bgcolor: 'rgba(239, 68, 68, 0.08)', borderLeft: `3px solid ${colors.border}` }),
                    }}
                    onClick={() => setExpandedUrgentIdx(isExpanded ? null : idx)}
                  >
                    <Box sx={{ width: 4, flexShrink: 0, ...(!isExpanded && { bgcolor: colors.border }), borderRadius: '2px 0 0 2px', my: '2px' }} />
                    <Box sx={{ width: 100, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                      <FormControl fullWidth size="small" variant="standard">
                        <Select
                          value={ticket.customer}
                          onChange={(e) => handleCustomerChange(idx, 'urgent', e.target.value)}
                          disableUnderline
                          displayEmpty
                          renderValue={(v) => (
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: v ? getCustomerColor(v as string).text : '#475569', px: 0.5 }}>
                              {(v as string) || '—'}
                            </Typography>
                          )}
                          sx={{ fontSize: '0.75rem' }}
                        >
                          {ALL_CUSTOMERS.map((c) => (
                            <MenuItem key={c} value={c} sx={{ fontSize: '0.8rem' }}>{c}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    <Box sx={{ width: 180, flexShrink: 0, display: 'flex', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <InlineField value={ticket.ticketNumber} onChange={(v) => setUrgentTickets((prev) => prev.map((t, i) => i === idx ? { ...t, ticketNumber: v } : t))} placeholder="Ticket #" mono />
                      {link && (
                        <Tooltip title="Open in Znuny" arrow>
                          <IconButton size="small" href={link} target="_blank" sx={{ p: 0.25, color: '#64748b' }}>
                            <OpenInNewIcon sx={{ fontSize: '0.75rem' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }} onClick={(e) => e.stopPropagation()}>
                      <InlineField value={ticket.ticketSubject} onChange={(v) => setUrgentTickets((prev) => prev.map((t, i) => i === idx ? { ...t, ticketSubject: v, lastUpdatedBy: loggedInName } : t))} placeholder="Subject" />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, mr: 1 }}>
                      <Tooltip title={hasNotes ? 'Has action notes — click to expand' : 'No notes yet — click to add'} arrow>
                        <IconButton size="small" sx={{ color: hasNotes ? '#4dd0e1' : '#64748b', '&:hover': { color: '#4dd0e1', bgcolor: 'rgba(0,188,212,0.08)' } }}>
                          <EditNoteIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                      <Tooltip title="Move to Current Tickets" arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentTickets((prev) => [...prev, {
                              ...emptyCurrentTicket(),
                              customer: ticket.customer,
                              ticketNumber: ticket.ticketNumber,
                              ticketSubject: ticket.ticketSubject,
                              znunyInstance: ticket.znunyInstance,
                              actionSummary: ticket.whatNeedsToBeDone,
                              lastUpdatedBy: ticket.lastUpdatedBy || loggedInName,
                            }]);
                            setUrgentTickets((prev) => prev.filter((_, i) => i !== idx));
                            setExpandedUrgentIdx(null);
                            setSnackbar({ open: true, message: `Moved #${ticket.ticketNumber} to Current`, severity: 'success' });
                          }}
                          sx={{ color: '#94a3b8', '&:hover': { color: '#00bcd4', bgcolor: 'rgba(0,188,212,0.08)' } }}
                        >
                          <KeyboardDoubleArrowDownIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); handleDelete('urgent', idx); }}
                        sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}
                      >
                        <DeleteIcon sx={{ fontSize: '0.875rem' }} />
                      </IconButton>
                    </Box>
                  </Box>
                  <Collapse in={isExpanded} timeout={200}>
                    <Box sx={{ px: 3, py: 2, borderLeft: '3px solid #ef4444', borderBottom: '1px solid rgba(255,255,255,0.06)', bgcolor: 'rgba(239,68,68,0.03)', background: 'linear-gradient(135deg, rgba(239,68,68,0.06) 0%, rgba(15,23,42,0.4) 100%)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                        {ticket.lastUpdatedBy && (
                          <Typography sx={{ fontSize: '0.7rem', color: '#64748b' }}>
                            By <span style={{ color: '#94a3b8', fontWeight: 600 }}>{ticket.lastUpdatedBy}</span>
                          </Typography>
                        )}
                        {link && (
                          <Button size="small" href={link} target="_blank" startIcon={<OpenInNewIcon sx={{ fontSize: '0.75rem !important' }} />} sx={{ fontSize: '0.65rem', color: '#64748b', textTransform: 'none', ml: 'auto', '&:hover': { color: '#4dd0e1' } }}>
                            Open in Znuny
                          </Button>
                        )}
                      </Box>
                      <Typography sx={{ fontSize: '0.65rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.1em', mb: 0.5, fontWeight: 600 }}>
                        What Needs to Be Done
                      </Typography>
                      <TextField
                        value={ticket.whatNeedsToBeDone}
                        onChange={(e) => setUrgentTickets((prev) => prev.map((t, i) => i === idx ? { ...t, whatNeedsToBeDone: e.target.value, lastUpdatedBy: loggedInName } : t))}
                        placeholder="Describe the urgent action required..."
                        multiline minRows={3} maxRows={8} fullWidth size="small"
                        sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.8125rem', color: '#e2e8f0', bgcolor: 'rgba(15,23,42,0.5)', borderRadius: '6px', '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' }, '&:hover fieldset': { borderColor: 'rgba(239,68,68,0.3)' }, '&.Mui-focused fieldset': { borderColor: '#ef4444', borderWidth: 1 } } }}
                      />
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </>
        )}
      </Paper>

      {/* ──────── CURRENT TICKETS ──────── */}
      <Paper
        id="section-current-tickets"
        sx={{
          mb: 2,
          overflow: 'hidden',
          bgcolor: 'rgba(15,23,42,0.6)',
          scrollMarginTop: 80,
          ...(filterHighlight?.type === 'stale' && { boxShadow: '0 0 0 2px #eab308, 0 0 18px rgba(234,179,8,0.35)' }),
          transition: 'box-shadow 0.4s ease',
        }}
      >
        <SectionBar
          title="Current Tickets"
          color="#00bcd4"
          count={currentTickets.length}
          onAdd={() => setCurrentTickets((prev) => [{ ...emptyCurrentTicket(), lastUpdatedBy: loggedInName }, ...prev])}
          addLabel="Add Ticket"
          info={undefined}
        />

        {currentTickets.length === 0 ? (
          <Typography sx={{ px: 3, py: 2, fontSize: '0.8rem', color: '#475569', fontStyle: 'italic' }}>
            No current tickets
          </Typography>
        ) : (
          <>
            <TableHeader columns={[
              { label: 'Customer', width: '100px' },
              { label: 'Ticket #', width: '180px' },
              { label: 'Ticket Subject', width: 'auto' },
            ]} />

            {groupedCurrentTickets.map(({ customer, tickets: groupTickets }) => {
              const colors = getCustomerColor(customer);
              const isCustomerHighlighted = filterHighlight?.type === 'customer' && filterHighlight.value === customer;
              return (
                <Box key={customer} id={`customer-group-${customer}`} sx={{ scrollMarginTop: 80 }}>
                  {/* Customer group header — subtle colored bar */}
                  <Box
                    aria-current={isCustomerHighlighted ? 'true' : undefined}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 2,
                      py: 0.5,
                      bgcolor: colors.bg,
                      borderLeft: `3px solid ${colors.border}`,
                      ...(isCustomerHighlighted && { boxShadow: `inset 0 0 0 2px ${colors.border}` }),
                      transition: 'box-shadow 0.4s ease',
                    }}
                  >
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: colors.text, letterSpacing: '0.05em' }}>
                      {customer}
                    </Typography>
                    <Typography sx={{ fontSize: '0.65rem', color: '#475569' }}>
                      {groupTickets.length} ticket{groupTickets.length !== 1 ? 's' : ''}
                    </Typography>
                  </Box>

                  {groupTickets.map(({ ticket, globalIdx }) => {
                    const link = znunyUrl(ticket.znunyInstance, ticket.ticketNumber);
                    const isExpanded = expandedTicketIdx === globalIdx;
                    const hasNotes = !!(ticket.actionSummary || ticket.nextSteps);
                    const isStaleHit = filterHighlight?.type === 'stale' && isStale(ticket);
                    return (
                      <Box key={globalIdx}>
                        {/* Compact summary row */}
                        <Box
                          aria-current={isStaleHit ? 'true' : undefined}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0,
                            px: 1,
                            py: 0.25,
                            borderBottom: isExpanded ? 'none' : '1px solid rgba(255,255,255,0.03)',
                            borderLeft: `3px solid ${colors.border}33`,
                            cursor: 'pointer',
                            '&:hover': { bgcolor: colors.bg },
                            transition: 'background 0.15s, box-shadow 0.4s ease',
                            ...(isExpanded && { bgcolor: colors.bg, borderLeft: `3px solid ${colors.border}` }),
                            ...(isStaleHit && {
                              bgcolor: 'rgba(234,179,8,0.10)',
                              boxShadow: 'inset 3px 0 0 0 #eab308',
                            }),
                          }}
                          onClick={() => setExpandedTicketIdx(isExpanded ? null : globalIdx)}
                        >
                          <Box sx={{ width: 4, flexShrink: 0 }} />
                          {/* Customer selector */}
                          <Box sx={{ width: 100, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                            <FormControl fullWidth size="small" variant="standard">
                              <Select
                                value={ticket.customer}
                                onChange={(e) => handleCustomerChange(globalIdx, 'current', e.target.value)}
                                disableUnderline
                                displayEmpty
                                renderValue={(v) => (
                                  <Typography sx={{ fontSize: '0.7rem', color: colors.text, px: 0.5, opacity: 0.5 }}>
                                    {(v as string) || '—'}
                                  </Typography>
                                )}
                                sx={{ fontSize: '0.75rem' }}
                              >
                                {ALL_CUSTOMERS.map((c) => (
                                  <MenuItem key={c} value={c} sx={{ fontSize: '0.8rem' }}>{c}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                          {/* Ticket # + Znuny link */}
                          <Box sx={{ width: 180, flexShrink: 0, display: 'flex', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                            <InlineField
                              value={ticket.ticketNumber}
                              onChange={(v) => setCurrentTickets((prev) => prev.map((t, i) => i === globalIdx ? { ...t, ticketNumber: v } : t))}
                              placeholder="Ticket #"
                              mono
                            />
                            {link && (
                              <Tooltip title="Open in Znuny" arrow>
                                <IconButton size="small" href={link} target="_blank" sx={{ p: 0.25, color: '#64748b' }}>
                                  <OpenInNewIcon sx={{ fontSize: '0.75rem' }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                          {/* Subject */}
                          <Box sx={{ flex: 1, minWidth: 0 }} onClick={(e) => e.stopPropagation()}>
                            <InlineField
                              value={ticket.ticketSubject}
                              onChange={(v) => setCurrentTickets((prev) => prev.map((t, i) => i === globalIdx ? { ...t, ticketSubject: v } : t))}
                              placeholder="Ticket subject"
                            />
                          </Box>
                          {/* Notes indicator + carry badge */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, mr: 1 }}>
                            {ticket.carriedForward && (
                              <Chip
                                label={`carried ${ticket.carriedForwardCount} shift${ticket.carriedForwardCount !== 1 ? 's' : ''}`}
                                size="small"
                                color="warning"
                                sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600 }}
                              />
                            )}
                            <Tooltip title={hasNotes ? 'Has notes — click to expand' : 'No notes yet — click to add'} arrow>
                              <IconButton size="small" sx={{ color: hasNotes ? '#4dd0e1' : '#64748b', '&:hover': { color: '#4dd0e1', bgcolor: 'rgba(0,188,212,0.08)' } }}>
                                <EditNoteIcon sx={{ fontSize: '1rem' }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          {/* Actions */}
                          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                            <Tooltip title="Escalate to Urgent" arrow>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setUrgentTickets((prev) => [...prev, {
                                    ...emptyUrgentTicket(),
                                    customer: ticket.customer,
                                    ticketNumber: ticket.ticketNumber,
                                    ticketSubject: ticket.ticketSubject,
                                    znunyInstance: ticket.znunyInstance,
                                    whatNeedsToBeDone: ticket.actionSummary || ticket.nextSteps,
                                  }]);
                                  setCurrentTickets((prev) => prev.filter((_, i) => i !== globalIdx));
                                  setExpandedTicketIdx(null);
                                  setSnackbar({ open: true, message: `Escalated #${ticket.ticketNumber} to Urgent`, severity: 'success' });
                                }}
                                sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444', bgcolor: 'rgba(239,68,68,0.08)' } }}
                              >
                                <KeyboardDoubleArrowUpIcon sx={{ fontSize: '1rem' }} />
                              </IconButton>
                            </Tooltip>
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); handleDelete('current', globalIdx); }}
                              sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}
                            >
                              <DeleteIcon sx={{ fontSize: '0.875rem' }} />
                            </IconButton>
                          </Box>
                        </Box>

                        {/* Expandable detail panel */}
                        <Collapse in={isExpanded} timeout={200}>
                          <Box
                            sx={{
                              px: 3,
                              py: 2,
                              borderLeft: `3px solid ${colors.border}`,
                              borderBottom: '1px solid rgba(255,255,255,0.06)',
                              bgcolor: 'rgba(0,188,212,0.03)',
                              background: `linear-gradient(135deg, ${colors.bg} 0%, rgba(15,23,42,0.4) 100%)`,
                            }}
                          >
                            {/* Meta info bar */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                              {ticket.lastUpdatedBy && (
                                <Typography sx={{ fontSize: '0.7rem', color: '#64748b' }}>
                                  Last updated by <span style={{ color: '#94a3b8', fontWeight: 600 }}>{ticket.lastUpdatedBy}</span>
                                </Typography>
                              )}
                              {ticket.firstAppearedDate && (
                                <Typography sx={{ fontSize: '0.7rem', color: '#475569' }}>
                                  On shift since {ticket.firstAppearedDate}
                                </Typography>
                              )}
                              {link && (
                                <Button
                                  size="small"
                                  href={link}
                                  target="_blank"
                                  startIcon={<OpenInNewIcon sx={{ fontSize: '0.75rem !important' }} />}
                                  sx={{
                                    fontSize: '0.65rem',
                                    color: '#64748b',
                                    textTransform: 'none',
                                    ml: 'auto',
                                    '&:hover': { color: '#4dd0e1' },
                                  }}
                                >
                                  Open in Znuny
                                </Button>
                              )}
                            </Box>

                            {/* Full-size text fields */}
                            <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: '0.65rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.1em', mb: 0.5, fontWeight: 600 }}>
                                  Action Summary
                                </Typography>
                                <TextField
                                  value={ticket.actionSummary}
                                  onChange={(e) => setCurrentTickets((prev) => prev.map((t, i) => i === globalIdx ? { ...t, actionSummary: e.target.value, lastUpdatedBy: loggedInName } : t))}
                                  placeholder="What was done on this ticket during the shift..."
                                  multiline
                                  minRows={3}
                                  maxRows={8}
                                  fullWidth
                                  size="small"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      fontSize: '0.8125rem',
                                      color: '#e2e8f0',
                                      bgcolor: 'rgba(15,23,42,0.5)',
                                      borderRadius: '6px',
                                      '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
                                      '&:hover fieldset': { borderColor: 'rgba(0,188,212,0.3)' },
                                      '&.Mui-focused fieldset': { borderColor: '#00bcd4', borderWidth: 1 },
                                    },
                                  }}
                                />
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: '0.65rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.1em', mb: 0.5, fontWeight: 600 }}>
                                  Next Steps
                                </Typography>
                                <TextField
                                  value={ticket.nextSteps}
                                  onChange={(e) => setCurrentTickets((prev) => prev.map((t, i) => i === globalIdx ? { ...t, nextSteps: e.target.value, lastUpdatedBy: loggedInName } : t))}
                                  placeholder="What needs to happen next on this ticket..."
                                  multiline
                                  minRows={3}
                                  maxRows={8}
                                  fullWidth
                                  size="small"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      fontSize: '0.8125rem',
                                      color: '#e2e8f0',
                                      bgcolor: 'rgba(15,23,42,0.5)',
                                      borderRadius: '6px',
                                      '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
                                      '&:hover fieldset': { borderColor: 'rgba(0,188,212,0.3)' },
                                      '&.Mui-focused fieldset': { borderColor: '#00bcd4', borderWidth: 1 },
                                    },
                                  }}
                                />
                              </Box>
                            </Box>
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </>
        )}
      </Paper>

      {/* ──────── MAINTENANCE ──────── */}
      <Paper
        id="section-maintenance"
        sx={{
          mb: 2,
          overflow: 'hidden',
          bgcolor: 'rgba(15,23,42,0.6)',
          scrollMarginTop: 80,
          ...(filterHighlight?.type === 'maintenance' && !filterHighlight.value && { boxShadow: '0 0 0 2px #f59e0b, 0 0 18px rgba(245,158,11,0.35)' }),
          transition: 'box-shadow 0.4s ease',
        }}
      >
        <SectionBar
          title="Maintenance / Scheduled Work"
          color="#f59e0b"
          count={maintenanceItems.length}
          onAdd={() => setMaintenanceItems((prev) => [emptyMaintenanceItem(), ...prev])}
          addLabel="Add Maint"
          icon={<BuildIcon sx={{ fontSize: '0.875rem', color: '#f59e0b' }} />}
        />
        <Typography sx={{ px: 3, pt: 0.5, pb: 0, fontSize: '0.7rem', color: '#64748b', fontStyle: 'italic' }}>
          Planned change windows and scheduled vendor work only. Incidents (outages, packet loss, service issues) belong in the tickets section above.
        </Typography>

        {maintenanceItems.length === 0 ? (
          <Typography sx={{ px: 3, py: 2, fontSize: '0.8rem', color: '#475569', fontStyle: 'italic' }}>
            No maintenance items
          </Typography>
        ) : (
          <>
            <TableHeader columns={[
              { label: 'Customer', width: '100px' },
              { label: 'Ticket #', width: '180px' },
              { label: 'Description', width: 'auto' },
            ]} />
            {maintenanceItems.map((item, idx) => {
              const colors = getCustomerColor(item.customer);
              const isExpanded = expandedMaintIdx === idx;
              const hasNotes = !!item.status;
              const isMaintHit =
                filterHighlight?.type === 'maintenance' &&
                !!filterHighlight.value &&
                item.ticketNumber === filterHighlight.value;
              return (
                <Box
                  key={idx}
                  id={item.ticketNumber ? `maintenance-ticket-${item.ticketNumber}` : undefined}
                  sx={{ scrollMarginTop: 80 }}
                >
                  <Box
                    aria-current={isMaintHit ? 'true' : undefined}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0,
                      px: 1,
                      py: 0.25,
                      borderBottom: isExpanded ? 'none' : '1px solid rgba(255,255,255,0.03)',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'rgba(245,158,11,0.04)' },
                      transition: 'background 0.15s, box-shadow 0.4s ease',
                      ...(isExpanded && { bgcolor: 'rgba(245,158,11,0.06)', borderLeft: `3px solid ${colors.border}` }),
                      ...(isMaintHit && {
                        bgcolor: 'rgba(245,158,11,0.12)',
                        boxShadow: 'inset 3px 0 0 0 #f59e0b',
                      }),
                    }}
                    onClick={() => setExpandedMaintIdx(isExpanded ? null : idx)}
                  >
                    <Box sx={{ width: 4, flexShrink: 0, ...(!isExpanded && { bgcolor: colors.border }), borderRadius: '2px 0 0 2px', my: '2px' }} />
                    <Box sx={{ width: 100, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                      <FormControl fullWidth size="small" variant="standard">
                        <Select
                          value={item.customer}
                          onChange={(e) => handleCustomerChange(idx, 'maintenance', e.target.value)}
                          disableUnderline
                          displayEmpty
                          renderValue={(v) => (
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: v ? getCustomerColor(v as string).text : '#ef4444', px: 0.5 }}>
                              {(v as string) || 'Required'}
                            </Typography>
                          )}
                          sx={{ fontSize: '0.75rem' }}
                        >
                          {MAINTENANCE_CUSTOMERS.map((c) => (
                            <MenuItem key={c} value={c} sx={{ fontSize: '0.8rem' }}>{c}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    <Box sx={{ width: 180, flexShrink: 0, display: 'flex', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <InlineField value={item.ticketNumber} onChange={(v) => setMaintenanceItems((prev) => prev.map((t, i) => i === idx ? { ...t, ticketNumber: normalizeTicketNumber(v) } : t))} placeholder="Ticket #" mono />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }} onClick={(e) => e.stopPropagation()}>
                      <InlineField value={item.description} onChange={(v) => setMaintenanceItems((prev) => prev.map((t, i) => i === idx ? { ...t, description: v } : t))} placeholder="Description" />
                    </Box>
                    <Box sx={{ width: 140, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                      <TextField
                        type="date"
                        value={item.scheduledDate || ''}
                        onChange={(e) => setMaintenanceItems((prev) => prev.map((t, i) => i === idx ? { ...t, scheduledDate: e.target.value || null } : t))}
                        size="small"
                        InputProps={{ sx: { fontSize: '0.75rem', color: '#e2e8f0', '& input': { py: 0.5, px: 1 } } }}
                        InputLabelProps={{ shrink: true }}
                        placeholder="Scheduled"
                        sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'transparent', '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' }, '&:hover fieldset': { borderColor: 'rgba(245,158,11,0.3)' } } }}
                      />
                    </Box>
                    <Box sx={{ width: 100, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                      <TextField
                        type="time"
                        value={item.scheduledTime || ''}
                        onChange={(e) => setMaintenanceItems((prev) => prev.map((t, i) => i === idx ? { ...t, scheduledTime: e.target.value || null } : t))}
                        size="small"
                        InputProps={{ sx: { fontSize: '0.75rem', color: '#e2e8f0', '& input': { py: 0.5, px: 1 } } }}
                        placeholder="Time"
                        sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'transparent', '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' }, '&:hover fieldset': { borderColor: 'rgba(245,158,11,0.3)' } } }}
                      />
                    </Box>
                    {item.scheduledDate && (
                      <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="View on Calendar" arrow>
                          <IconButton
                            size="small"
                            aria-label={`View ${item.ticketNumber ? `ticket ${item.ticketNumber} ` : ''}on Calendar`}
                            onClick={() => {
                              const month = item.scheduledDate!.slice(0, 7);
                              const params = new URLSearchParams({ month });
                              if (item.ticketNumber) params.set('ticket', item.ticketNumber);
                              navigate(`/calendar?${params.toString()}`);
                            }}
                            sx={{ color: '#64748b', '&:hover': { color: '#f59e0b', bgcolor: 'rgba(245,158,11,0.08)' } }}
                          >
                            <EventIcon sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, mr: 1 }}>
                      <Tooltip title={hasNotes ? 'Has status notes — click to expand' : 'No notes yet — click to add'} arrow>
                        <IconButton size="small" sx={{ color: hasNotes ? '#4dd0e1' : '#64748b', '&:hover': { color: '#4dd0e1', bgcolor: 'rgba(0,188,212,0.08)' } }}>
                          <EditNoteIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); handleDelete('maintenance', idx); }}
                        sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}
                      >
                        <DeleteIcon sx={{ fontSize: '0.875rem' }} />
                      </IconButton>
                    </Box>
                  </Box>
                  <Collapse in={isExpanded} timeout={200}>
                    <Box sx={{ px: 3, py: 2, borderLeft: '3px solid #f59e0b', borderBottom: '1px solid rgba(255,255,255,0.06)', bgcolor: 'rgba(245,158,11,0.03)', background: 'linear-gradient(135deg, rgba(245,158,11,0.06) 0%, rgba(15,23,42,0.4) 100%)' }}>
                      <Typography sx={{ fontSize: '0.65rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.1em', mb: 0.5, fontWeight: 600 }}>
                        Status / Notes
                      </Typography>
                      <TextField
                        value={item.status}
                        onChange={(e) => setMaintenanceItems((prev) => prev.map((t, i) => i === idx ? { ...t, status: e.target.value } : t))}
                        placeholder="Current status, scheduled dates, notes..."
                        multiline minRows={3} maxRows={8} fullWidth size="small"
                        sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.8125rem', color: '#e2e8f0', bgcolor: 'rgba(15,23,42,0.5)', borderRadius: '6px', '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' }, '&:hover fieldset': { borderColor: 'rgba(245,158,11,0.3)' }, '&.Mui-focused fieldset': { borderColor: '#f59e0b', borderWidth: 1 } } }}
                      />
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </>
        )}
      </Paper>

      {/* ──────── TRASH BIN ──────── */}
      <Paper sx={{ mb: 2, overflow: 'hidden', bgcolor: 'rgba(15,23,42,0.6)' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 1,
            px: 2,
            background: 'linear-gradient(90deg, rgba(100,116,139,0.12) 0%, transparent 100%)',
            borderLeft: '3px solid #64748b',
            borderRadius: '0 6px 6px 0',
            cursor: 'pointer',
            '&:hover': { bgcolor: 'rgba(100,116,139,0.08)' },
          }}
          onClick={() => setTrashExpanded(!trashExpanded)}
        >
          <DeleteSweepIcon sx={{ fontSize: '0.875rem', color: '#64748b' }} />
          <Typography sx={{ color: '#64748b', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: '0.75rem' }}>
            Trash
          </Typography>
          {trashItems.length > 0 && (
            <Chip label={trashItems.length} size="small" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700, bgcolor: 'rgba(100,116,139,0.22)', color: '#64748b', border: '1px solid rgba(100,116,139,0.44)' }} />
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button
            startIcon={<HistoryIcon sx={{ fontSize: '0.875rem !important' }} />}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              (async () => {
                try {
                  const data = await listShiftSummaries(20);
                  setHistorySummaries(data);
                  setHistoryDialogOpen(true);
                } catch (err) {
                  console.error('Failed to load history:', err);
                }
              })();
            }}
            sx={{ fontSize: '0.7rem', color: '#64748b', borderColor: 'rgba(100,116,139,0.44)', textTransform: 'none', px: 1.5, py: 0.25, minHeight: 0, border: '1px solid', borderRadius: '4px', '&:hover': { bgcolor: 'rgba(100,116,139,0.15)', borderColor: '#64748b' } }}
          >
            Browse Past Shifts
          </Button>
        </Box>

        <Collapse in={trashExpanded} timeout={200}>
          {trashItems.length === 0 ? (
            <Typography sx={{ px: 3, py: 2, fontSize: '0.8rem', color: '#475569', fontStyle: 'italic' }}>
              Trash is empty
            </Typography>
          ) : (
            <Box>
              {trashItems.map((item) => {
                const ticket = item.ticket;
                const tn = 'ticketNumber' in ticket ? ticket.ticketNumber : '';
                const subject = 'ticketSubject' in ticket ? ticket.ticketSubject : ('description' in ticket ? ticket.description : '');
                const customer = ticket.customer || '';
                const colors = getCustomerColor(customer);
                const typeColors: Record<string, string> = { urgent: '#ef4444', current: '#00bcd4', maintenance: '#f59e0b' };
                return (
                  <Box
                    key={item.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 2,
                      py: 0.75,
                      borderBottom: '1px solid rgba(255,255,255,0.03)',
                      '&:hover': { bgcolor: 'rgba(100,116,139,0.06)' },
                    }}
                  >
                    <Box sx={{ width: 4, flexShrink: 0, bgcolor: colors.border, borderRadius: 1, alignSelf: 'stretch' }} />
                    <Chip
                      label={item.type === 'maintenance' ? 'Maint' : item.type.charAt(0).toUpperCase() + item.type.slice(1)}
                      size="small"
                      sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600, bgcolor: `${typeColors[item.type]}22`, color: typeColors[item.type], border: `1px solid ${typeColors[item.type]}44`, minWidth: 52 }}
                    />
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: colors.text, minWidth: 50 }}>
                      {customer || '—'}
                    </Typography>
                    {tn && (
                      <Typography sx={{ fontSize: '0.7rem', fontFamily: '"JetBrains Mono", monospace', color: '#94a3b8', minWidth: 80 }}>
                        #{tn}
                      </Typography>
                    )}
                    <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {subject}
                    </Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: '#475569', flexShrink: 0, minWidth: 50, textAlign: 'right' }}>
                      {relativeTime(item.deletedAt)}
                    </Typography>
                    <Tooltip title="Restore to form" arrow>
                      <IconButton size="small" onClick={() => handleRestoreFromTrash(item.id)} sx={{ color: '#10b981', '&:hover': { color: '#34d399', bgcolor: 'rgba(16,185,129,0.08)' } }}>
                        <RestoreFromTrashIcon sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete permanently" arrow>
                      <IconButton size="small" onClick={() => handlePermanentDelete(item.id)} sx={{ color: '#64748b', '&:hover': { color: '#ef4444', bgcolor: 'rgba(239,68,68,0.08)' } }}>
                        <DeleteForeverIcon sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                );
              })}
              <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'flex-end' }}>
                <Button size="small" onClick={handleClearTrash} sx={{ fontSize: '0.65rem', color: '#64748b', textTransform: 'none', '&:hover': { color: '#ef4444' } }}>
                  Clear All Trash
                </Button>
              </Box>
            </Box>
          )}
        </Collapse>
      </Paper>

      {/* ──────── BROWSE PAST SHIFTS DIALOG ──────── */}
      <Dialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#0f172a', color: '#e2e8f0', borderRadius: '12px', border: '1px solid rgba(0,188,212,0.2)' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <HistoryIcon sx={{ color: '#00bcd4' }} />
          <Typography variant="h6" fontWeight={600}>Browse Past Shifts</Typography>
          <Typography variant="body2" sx={{ ml: 'auto', color: '#64748b' }}>Click a shift to see tickets, then add to current form</Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { color: '#64748b', fontSize: '0.7rem', textTransform: 'uppercase', borderColor: 'rgba(255,255,255,0.06)' } }}>
                <TableCell>Date</TableCell>
                <TableCell>Shift</TableCell>
                <TableCell>Tech</TableCell>
                <TableCell align="center">Tickets</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {historySummaries.map((s) => {
                const isExp = historyExpandedId === s.id;
                const parseJson = (json: string | null | undefined) => {
                  try { let p = JSON.parse(json ?? '[]'); if (typeof p === 'string') p = JSON.parse(p); return Array.isArray(p) ? p : []; } catch { return []; }
                };
                const urgent = parseJson(s.urgentTicketsJson) as UrgentTicket[];
                const current = parseJson(s.currentTicketsJson) as CurrentTicket[];
                const maint = parseJson(s.maintenanceItemsJson) as MaintenanceItem[];
                const total = urgent.length + current.length + maint.length;
                return (
                  <React.Fragment key={s.id}>
                    <TableRow
                      hover
                      sx={{ cursor: 'pointer', '& td': { borderColor: 'rgba(255,255,255,0.04)' } }}
                      onClick={() => setHistoryExpandedId(isExp ? null : s.id)}
                    >
                      <TableCell>
                        <Typography fontWeight={500} fontSize="0.8rem">
                          {new Date(s.date + 'T00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={s.shift} size="small" sx={{ fontSize: '0.65rem', fontWeight: 500, bgcolor: s.shift === 'NIGHT' ? '#5c6bc0' : s.shift === 'DAY' ? '#ffa726' : '#ab47bc', color: 'white' }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{s.onDutyTech}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{total}</TableCell>
                    </TableRow>
                    {isExp && (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ py: 0, bgcolor: 'rgba(0,188,212,0.03)', borderColor: 'rgba(255,255,255,0.06)' }}>
                          <Box sx={{ py: 1.5, px: 1 }}>
                            {urgent.length > 0 && (
                              <Box sx={{ mb: 1 }}>
                                <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#ef4444', textTransform: 'uppercase', mb: 0.5 }}>Urgent</Typography>
                                {urgent.map((t, i) => (
                                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
                                    <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', flex: 1 }}>
                                      <strong>{t.customer}</strong> — #{t.ticketNumber} {t.ticketSubject}
                                    </Typography>
                                    <Tooltip title="Add to current form as Urgent" arrow>
                                      <IconButton size="small" onClick={() => {
                                        setUrgentTickets((prev) => [...prev, { ...t, lastUpdatedBy: loggedInName }]);
                                        setSnackbar({ open: true, message: `Added #${t.ticketNumber} as Urgent`, severity: 'success' });
                                      }} sx={{ color: '#00bcd4', '&:hover': { bgcolor: 'rgba(0,188,212,0.1)' } }}>
                                        <AddCircleOutlineIcon sx={{ fontSize: '0.875rem' }} />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                ))}
                              </Box>
                            )}
                            {current.length > 0 && (
                              <Box sx={{ mb: 1 }}>
                                <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#00bcd4', textTransform: 'uppercase', mb: 0.5 }}>Current</Typography>
                                {current.map((t, i) => (
                                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
                                    <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', flex: 1 }}>
                                      <strong>{t.customer}</strong> — #{t.ticketNumber} {t.ticketSubject}
                                    </Typography>
                                    <Tooltip title="Add to current form" arrow>
                                      <IconButton size="small" onClick={() => {
                                        setCurrentTickets((prev) => [...prev, { ...emptyCurrentTicket(), ...t, lastUpdatedBy: loggedInName }]);
                                        setSnackbar({ open: true, message: `Added #${t.ticketNumber}`, severity: 'success' });
                                      }} sx={{ color: '#00bcd4', '&:hover': { bgcolor: 'rgba(0,188,212,0.1)' } }}>
                                        <AddCircleOutlineIcon sx={{ fontSize: '0.875rem' }} />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                ))}
                              </Box>
                            )}
                            {maint.length > 0 && (
                              <Box>
                                <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase', mb: 0.5 }}>Maintenance</Typography>
                                {maint.map((t, i) => (
                                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
                                    <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', flex: 1 }}>
                                      <strong>{t.customer}</strong> — #{t.ticketNumber} {t.description}
                                    </Typography>
                                    <Tooltip title="Add to current form as Maintenance" arrow>
                                      <IconButton size="small" onClick={() => {
                                        setMaintenanceItems((prev) => [...prev, { ...emptyMaintenanceItem(), ...t }]);
                                        setSnackbar({ open: true, message: `Added #${t.ticketNumber} as Maintenance`, severity: 'success' });
                                      }} sx={{ color: '#00bcd4', '&:hover': { bgcolor: 'rgba(0,188,212,0.1)' } }}>
                                        <AddCircleOutlineIcon sx={{ fontSize: '0.875rem' }} />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                ))}
                              </Box>
                            )}
                            {total === 0 && <Typography sx={{ fontSize: '0.75rem', color: '#475569', fontStyle: 'italic' }}>No tickets in this shift</Typography>}
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>

      {/* ──────── NOTES + SUBMIT ──────── */}
      <Paper sx={{ p: 2, mb: 2, bgcolor: 'rgba(15,23,42,0.6)' }}>
        <TextField
          label="Shift Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          fullWidth
          multiline
          rows={2}
          size="small"
          placeholder="General notes about the shift..."
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {urgentTickets.length > 0 && (
              <Chip label={`${urgentTickets.length} urgent`} size="small" sx={{ bgcolor: 'rgba(239,68,68,0.15)', color: '#ef4444', fontSize: '0.7rem' }} />
            )}
            <Chip label={`${currentTickets.length} current`} size="small" sx={{ bgcolor: 'rgba(0,188,212,0.15)', color: '#00bcd4', fontSize: '0.7rem' }} />
            {maintenanceItems.length > 0 && (
              <Chip label={`${maintenanceItems.length} maintenance`} size="small" sx={{ bgcolor: 'rgba(245,158,11,0.15)', color: '#f59e0b', fontSize: '0.7rem' }} />
            )}
            <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.08)', mx: 0.5 }} />
            <Tooltip title="Undo (Ctrl+Z)" arrow>
              <span>
                <IconButton size="small" onClick={handleUndo} disabled={!canUndo} sx={{ color: canUndo ? '#94a3b8' : '#334155', '&:hover': { color: '#4dd0e1', bgcolor: 'rgba(0,188,212,0.08)' } }}>
                  <UndoIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Redo (Ctrl+Shift+Z)" arrow>
              <span>
                <IconButton size="small" onClick={handleRedo} disabled={!canRedo} sx={{ color: canRedo ? '#94a3b8' : '#334155', '&:hover': { color: '#4dd0e1', bgcolor: 'rgba(0,188,212,0.08)' } }}>
                  <RedoIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
              </span>
            </Tooltip>
            {draftStatus === 'saved' && (
              <Typography sx={{ fontSize: '0.65rem', color: '#10b981', fontStyle: 'italic', ml: 0.5 }}>
                Draft saved
              </Typography>
            )}
            {draftStatus === 'saving' && (
              <Typography sx={{ fontSize: '0.65rem', color: '#64748b', fontStyle: 'italic', ml: 0.5 }}>
                Saving...
              </Typography>
            )}
          </Box>
          <Button
            variant="contained"
            endIcon={<SendIcon />}
            onClick={handleSubmit}
            disabled={submitting}
            sx={{
              background: 'linear-gradient(135deg, #1a7a8a, #00bcd4)',
              fontWeight: 600,
              px: 3,
              '&:hover': { background: 'linear-gradient(135deg, #0d4f5c, #0097a7)' },
            }}
          >
            {submitting ? 'Submitting...' : 'Submit Shift Summary'}
          </Button>
        </Box>
      </Paper>

      {/* Status Quo Confirmation Dialog */}
      <Dialog
        open={statusQuoConfirmOpen}
        onClose={() => setStatusQuoConfirmOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: '#1e293b',
            border: '1px solid rgba(245,158,11,0.3)',
            borderRadius: '12px',
            maxWidth: 440,
          },
        }}
      >
        <DialogTitle sx={{ color: '#f59e0b', fontWeight: 700, fontSize: '1rem', pb: 1 }}>
          Confirm Status Quo
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.85rem', color: '#cbd5e1', mb: 2 }}>
            This will create a shift record for <strong>{SHIFT_LABELS[shift]}</strong> on <strong>{date}</strong> carrying
            forward all items from the previous shift with no changes.
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, mb: 1 }}>
            {urgentTickets.length > 0 && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#ef4444' }}>{urgentTickets.length}</Typography>
                <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', textTransform: 'uppercase' }}>Urgent</Typography>
              </Box>
            )}
            {currentTickets.length > 0 && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#4dd0e1' }}>{currentTickets.length}</Typography>
                <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', textTransform: 'uppercase' }}>Current</Typography>
              </Box>
            )}
            {maintenanceItems.length > 0 && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#f59e0b' }}>{maintenanceItems.length}</Typography>
                <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', textTransform: 'uppercase' }}>Maintenance</Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setStatusQuoConfirmOpen(false)}
            sx={{ color: '#94a3b8', textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleStatusQuo}
            disabled={submitting}
            startIcon={<RepeatIcon />}
            sx={{
              bgcolor: '#f59e0b',
              color: '#0f172a',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': { bgcolor: '#d97706' },
            }}
          >
            {submitting ? 'Submitting...' : 'Carry Forward'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={lastDeletedRef.current ? 6000 : 4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          action={lastDeletedRef.current ? (
            <Button color="inherit" size="small" onClick={handleRestoreLast} sx={{ fontWeight: 700, textTransform: 'none' }}>
              Restore
            </Button>
          ) : undefined}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ShiftHandoffForm;
